import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  alpha
} from '@mui/material';
import { Draggable } from 'react-beautiful-dnd';

const LeadCard = ({ lead, index, onViewDetails, columnColor, columnGradient }) => {
  const getLeadName = () => {
    return lead.fullName || `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || 'Unknown Lead';
  };

  const getLeadEmail = () => {
    return lead.email || 'No email provided';
  };

  const getLeadPhone = () => {
    return lead.phoneNumber || lead.phone || 'No phone provided';
  };

  const getPlanTypeColor = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'enterprise':
        return { 
          bgcolor: '#fef3c7', 
          color: '#d97706',
          borderColor: '#f59e0b'
        };
      case 'professional':
        return { 
          bgcolor: '#e0e7ff', 
          color: '#4338ca',
          borderColor: '#6366f1'
        };
      case 'starter':
        return { 
          bgcolor: '#f0f9ff', 
          color: '#0284c7',
          borderColor: '#0ea5e9'
        };
      default:
        return { 
          bgcolor: '#f3f4f6', 
          color: '#6b7280',
          borderColor: '#9ca3af'
        };
    }
  };

  const truncateText = (text, maxLength = 25) => {
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + '...';
    }
    return text || '';
  };

  const handleCardClick = () => {
    onViewDetails(lead);
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'Invalid date';
    }
  };

  return (
    <Draggable draggableId={lead.leadId} index={index}>
      {(provided, snapshot) => (
        <Card
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={handleCardClick}
          sx={{
            cursor: 'pointer',
            transform: snapshot.isDragging ? 'rotate(3deg) scale(1.05)' : 'none',
            boxShadow: snapshot.isDragging 
              ? `0 15px 30px -5px ${alpha(columnColor, 0.4)}, 0 10px 20px -5px ${alpha(columnColor, 0.2)}`
              : `0 2px 8px ${alpha(columnColor, 0.1)}, 0 1px 4px ${alpha(columnColor, 0.05)}`,
            '&:hover': {
              boxShadow: `0 8px 20px -5px ${alpha(columnColor, 0.2)}, 0 4px 12px -2px ${alpha(columnColor, 0.1)}`,
              transform: 'translateY(-2px)',
            },
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            borderRadius: 3,
            background: snapshot.isDragging 
              ? `linear-gradient(145deg, ${alpha(columnColor, 0.05)} 0%, #ffffff 100%)`
              : 'linear-gradient(145deg, #ffffff 0%, #fafbfc 100%)',
            border: `1px solid ${snapshot.isDragging ? columnColor : alpha(columnColor, 0.1)}`,
            overflow: 'hidden',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: columnGradient || columnColor,
              opacity: snapshot.isDragging ? 1 : 0.8
            }
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Compact Header - No more menu button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-start', 
              alignItems: 'flex-start',
              mb: 1.5,
              width: '100%'
            }}>
              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    lineHeight: 1.2,
                    color: '#1e293b',
                    mb: 0.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={getLeadName()}
                >
                  {truncateText(getLeadName(), 22)} {/* Increased character limit since no menu button */}
                </Typography>
                
                {lead.planType && (
                  <Chip
                    label={lead.planType.charAt(0).toUpperCase() + lead.planType.slice(1)}
                    size="small"
                    sx={{
                      ...getPlanTypeColor(lead.planType),
                      fontSize: '0.65rem',
                      height: 20,
                      fontWeight: 600,
                      borderRadius: 1.5,
                      '& .MuiChip-label': { px: 1 }
                    }}
                  />
                )}
              </Box>
            </Box>

            {/* Compact Contact Information */}
            <Box sx={{ mb: 1.5 }}>
              {/* Email */}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.75rem',
                  color: '#475569',
                  fontWeight: 500,
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={getLeadEmail()}
              >
                📧 {truncateText(getLeadEmail(), 26)} {/* Increased character limit */}
              </Typography>
              
              {/* Phone */}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontSize: '0.75rem', 
                  color: '#475569',
                  fontWeight: 500,
                  mb: 0.5,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
                title={getLeadPhone()}
              >
                📱 {truncateText(getLeadPhone(), 22)} {/* Increased character limit */}
              </Typography>

              {/* Location */}
              {lead.location && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontSize: '0.75rem', 
                    color: '#475569',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                  title={lead.location}
                >
                  📍 {truncateText(lead.location, 24)} {/* Increased character limit */}
                </Typography>
              )}
            </Box>

            {/* Compact Footer */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              pt: 1,
              borderTop: `1px solid ${alpha(columnColor, 0.1)}`
            }}>
              <Typography variant="caption" sx={{ 
                fontSize: '0.65rem', 
                color: '#64748b', 
                fontWeight: 500
              }}>
                {formatDate(lead.createdDate || lead.createdAt)}
              </Typography>
              
              {lead.source && (
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem',
                  color: columnColor,
                  fontWeight: 600,
                  bgcolor: alpha(columnColor, 0.1),
                  px: 1,
                  py: 0.25,
                  borderRadius: 1
                }}>
                  {truncateText(lead.source, 10)} {/* Increased character limit */}
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>
      )}
    </Draggable>
  );
};

export default LeadCard;